import { formatIsoForTimezone, normalizeTimezone, timezoneAbbreviation } from '@/lib/timezone';

const EMPTY = '—';

/** "42s" / "3m 12s" / "2h 5m"; em dash for missing or non-positive values. */
export function formatDurationWords(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds <= 0) return EMPTY;
  const m = Math.floor(totalSeconds / 60);
  const s = Math.round(totalSeconds % 60);
  if (m === 0) return `${s}s`;
  if (m < 60) return `${m}m ${s}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** "42s" / "3m" / "3m 12s"; zero renders as "0s" (used for talk-time labels). */
export function formatDurationSeconds(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  const rem = seconds % 60;
  if (minutes <= 0) return `${rem}s`;
  if (rem === 0) return `${minutes}m`;
  return `${minutes}m ${rem}s`;
}

/** "0:00" / "3:07" clock style (audio players, call leg timings). */
export function formatDurationClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** "45m" / "2h 5m"; sub-minute values render as "0m" (timeline blocks, aggregates). */
export function formatDurationHoursMinutes(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

/**
 * With a timezone: medium date + short time in that zone.
 * Without: browser-local "Mon D, HH:MM:SS" (event feeds where seconds matter).
 */
export function formatDateTime(iso: string | null | undefined, timezone?: string): string {
  if (!iso) return EMPTY;
  if (timezone) return formatIsoForTimezone(iso, timezone);
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * "3 Jul, 17:50 CDT" — 24h wall-clock in the given zone with its live abbreviation
 * inline (call logs and other absolute event times). Other years spell it out:
 * "3 Jul 2025, 17:50 CDT".
 */
export function formatClockWithZone(iso: string | null | undefined, timezone: string): string {
  if (!iso) return EMPTY;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return EMPTY;
  const tz = normalizeTimezone(timezone);
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const part = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  const parts = formatter.formatToParts(date);
  const year = part(parts, 'year');
  const currentYear = part(formatter.formatToParts(new Date()), 'year');
  const yearSuffix = year === currentYear ? '' : ` ${year}`;
  return `${part(parts, 'day')} ${part(parts, 'month')}${yearSuffix}, ${part(parts, 'hour')}:${part(parts, 'minute')} ${timezoneAbbreviation(tz, date)}`;
}

/**
 * "just now" / "5m ago" / "3h ago" / "1d 4h ago" for past timestamps.
 * Hour granularity up to 7 days; plain days beyond ("8d ago").
 */
export function formatRelativeTime(iso: string): string {
  const mins = Math.floor((Date.now() - Date.parse(iso)) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) {
    const remHours = hours % 24;
    return remHours > 0 ? `${days}d ${remHours}h ago` : `${days}d ago`;
  }
  return `${days}d ago`;
}

/**
 * Day-granular "how long ago" for a date-only string ('YYYY-MM-DD').
 * Use this (not formatRelativeTime) when the source has no real timestamp, so we
 * never claim more precision than a day (e.g. 'today', 'yesterday', '3d ago').
 */
export function formatRelativeDate(date: string): string {
  const then = new Date(`${date}T00:00:00`);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((startOfToday.getTime() - then.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days / 7)}w ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
}

export function formatPercent(numerator: number, denominator: number, digits = 0): string {
  if (denominator <= 0) return EMPTY;
  const factor = 10 ** digits;
  return `${Math.round((numerator / denominator) * 100 * factor) / factor}%`;
}

export function formatNumber(value: number | null | undefined, digits = 1, suffix = ''): string {
  if (value == null || !Number.isFinite(value)) return EMPTY;
  return `${value.toFixed(digits)}${suffix}`;
}

export function formatScore(value: number | null | undefined, suffix = '/10'): string {
  if (value == null || !Number.isFinite(value)) return EMPTY;
  return `${value.toFixed(1)}${suffix}`;
}

/** "no_show" → "No show", "setter" → "Setter", "maya" → "Maya" — sentence-case an enum/username. */
export function humanizeLabel(value: string | null | undefined): string {
  const words = String(value ?? '').replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}
